import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Dropdown } from "react-bootstrap";
import { format } from "date-fns";
import "react-datepicker/dist/react-datepicker.css";

import "../common/Util.css";
import "../common/skeleton-effect.css";
import { ReactComponent as SearchIc } from "../../assets/svg/search.svg";
import { ReactComponent as AddIc } from "../../assets/svg/add_icon.svg";
import { ReactComponent as DownArrowIc } from "../../assets/svg/down_arrow_ics.svg";
import { ReactComponent as RarIc } from "../../assets/svg/rar_ic.svg";
import { ReactComponent as LarIc } from "../../assets/svg/lar_ic.svg";
import { ReactComponent as EmailView } from "../../assets/svg/email_view_ic.svg";
import { ReactComponent as ListView } from "../../assets/svg/list_view_ic.svg";
import { ReactComponent as SelectGrid } from "../../assets/svg/seelct_grid_ic.svg";
import { ReactComponent as SelectList } from "../../assets/svg/select_list_ic.svg";

import { getMilestonesListApi, deleteMilestoneApi } from "./milestone-api";
import { useGlobal } from "../../GlobalContext";
import { sanitizeInput } from "../common/security-utils";
import CommonSnackbar, { CommonPagination } from "../common/reusable-commponent";

import DeleteModal from "../common/deleteModel";
import DateRangePicker from "../common/dateRange";
import editIcon from "../../assets/svg/edit_ic.svg";
import deleteIcon from "../../assets/svg/delete_ic.svg";
import moreIcon from "../../assets/svg/more_ic.svg";
import milestoneIcon from "../../assets/svg/milestone_active.svg";

const ICON_STYLE = { width: "16px", height: "16px" };

const SORT_BY_OPTIONS = [
  { value: "created asc", label: "Created (Oldest)" },
  { value: "created desc", label: "Created (Newest)" },
  { value: "name asc", label: "Name (A - Z)" },
  { value: "name desc", label: "Name (Z - A)" },
];

export const MilestoneListing = () => {
  const { rolePermissionList } = useGlobal();
  const navigate = useNavigate();

  // States
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedSortby, setSelectedSortBy] = useState(SORT_BY_OPTIONS[1]);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [active, setActive] = useState("list");
  const [pageCount, setPageCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [itemOffset, setItemOffset] = useState(0);
  const [deleteId, setDeleteId] = useState(null);
  const [isOpenDelete, setIsOpenDelete] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [milestonesList, setMilestonesList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ show: false, message: "", status: null });

  // Filter States
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [startDateSave, setStartDateSave] = useState(null);
  const [endDateSave, setEndDateSave] = useState(null);

  // Permission Checker
  const checkPermission = useCallback(
    (permission) => {
      if (!rolePermissionList || !Array.isArray(rolePermissionList)) return false;
      return rolePermissionList.some(
        (element) => element?.["name"] === "Milestone" && element?.["menu_permission"] === true && element?.[permission] === true
      );
    },
    [rolePermissionList]
  );

  const handleSnackbarClose = useCallback(() => setSnackbar((prev) => ({ ...prev, show: false })), []);

  const triggerSnackbar = useCallback((message, status) => {
    setSnackbar({ show: true, message: sanitizeInput(message), status });
  }, []);

  // Debounce search
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(handler);
  }, [search]);

  // Open Delete Modal
  useEffect(() => {
    if (deleteId) {
      setIsOpenDelete(true);
    }
  }, [deleteId]);

  const handleDateChange = useCallback(({ start, end }) => {
    setStartDate(start);
    setEndDate(end);
    setStartDateSave(start ? format(start, "yyyy-MM-dd") : null);
    setEndDateSave(end ? format(end, "yyyy-MM-dd") : null);
  }, []);

  const handlePageClick = useCallback((event) => {
    setItemOffset(event.selected);
  }, []);

  const getMilestonesList = useCallback(async () => {
    setIsLoading(true);
    const [field, dir] = selectedSortby?.value?.split(" ") || ["created", "desc"];
    const params = {
      limit: itemsPerPage,
      skip: itemOffset * itemsPerPage,
      search: sanitizeInput(debouncedSearch),
      from_date: startDateSave,
      to_date: endDateSave,
      sort_field: field,
      sort_dir: dir,
    };

    try {
      const response = await getMilestonesListApi(params);
      if (response?.data?.success === true) {
        setTotalCount(response.data.totalCount ?? 0);
        setPageCount(response.data.totalPages ?? 0);
        setMilestonesList(response.data.Milestone ?? []);
      }
    } catch (err) {
      // Error handled by api-service
    } finally {
      setIsLoading(false);
    }
  }, [itemsPerPage, itemOffset, debouncedSearch, selectedSortby, startDateSave, endDateSave]);

  useEffect(() => {
    getMilestonesList();
  }, [getMilestonesList]);

  const deleteMilestone = useCallback(async () => {
    if (!deleteId) return;
    try {
      setIsLoading(true);
      const response = await deleteMilestoneApi(deleteId);
      if (response?.data?.success === true) {
        getMilestonesList();
        triggerSnackbar(response.data.message || "Milestone deleted successfully", true);
        setDeleteId(null);
        setIsOpenDelete(false);
      } else {
        triggerSnackbar(response?.data?.message || "Failed to delete milestone", false);
      }
    } catch (err) {
      triggerSnackbar("An error occurred during deletion", false);
    } finally {
      setIsLoading(false);
    }
  }, [deleteId, getMilestonesList, triggerSnackbar]);

  const resetFilters = useCallback(() => {
    setStartDate(null);
    setEndDate(null);
    setStartDateSave(null);
    setEndDateSave(null);
    setSearch("");
    setSelectedSortBy(SORT_BY_OPTIONS[1]);
    setItemOffset(0);
  }, []);

  return (
    <>
      <DeleteModal
        title={"milestone"}
        isOpen={isOpenDelete}
        onClose={() => {
          setIsOpenDelete(false);
          setDeleteId(null);
        }}
        onDelete={deleteMilestone}
      />

      <div className="milestone-list-container">
        <div className="pt-2 pb-2">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <div className="my-heading fs-24 fw-600" style={{ color: "#9299A7" }}>
              MILESTONES : {totalCount}
            </div>

            <div className="d-flex flex-wrap gap-3">
              <div className="search-wrapper d-flex align-items-center gap-2 px-3 py-2">
                <SearchIc />
                <input
                  type="text"
                  placeholder="Search"
                  className="border-0 outline-0 rounded-3 fs-14 fw-500 textcolor"
                  value={search || ""}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              {checkPermission("create_permission") && (
                <div
                  className="cursor-pointer add-new d-flex align-items-center justify-content-center gap-2 px-3"
                  onClick={() => navigate("/milestone/create")}
                  style={{ height: "40px" }}
                >
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <AddIc style={{ width: "16px", height: "16px" }} />
                  </div>
                  <div className="fs-12 fw-600" style={{ lineHeight: "16px" }}>
                    Add Milestone
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex flex-wrap justify-content-start gap-2">
              <div onClick={(e) => e.stopPropagation()}>
                <Dropdown>
                  <Dropdown.Toggle
                    variant="light"
                    id="dropdown-sort"
                    className="d-flex align-items-center justify-content-between"
                    style={{
                      borderRadius: "12px",
                      border: "1px solid #EBEBEB",
                      padding: "6px 12px",
                      backgroundColor: "white",
                    }}
                    title={selectedSortby.label}
                  >
                    <span className="fs-12 fw-400 textGrey">Sort by :</span>
                    <span className="d-flex align-items-center px-1">
                      <span className="dropdown-text">{selectedSortby.label}</span>
                    </span>
                  </Dropdown.Toggle>

                  <Dropdown.Menu
                    style={{
                      backgroundColor: "white",
                      borderRadius: "8px",
                      border: "1px solid #EBEBEB",
                      boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                    }}
                  >
                    {SORT_BY_OPTIONS.map((option) => (
                      <Dropdown.Item
                        className="fs-14 fw-500 textcolor"
                        key={option.value}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedSortBy(option);
                          setItemOffset(0);
                        }}
                        active={selectedSortby.value === option.value}
                      >
                        {option.label}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              </div>

              <div
                className="d-flex align-items-center px-1"
                style={{
                  borderRadius: "8px",
                  height: "32px",
                  border: "1px solid #EBEBEB",
                  backgroundColor: "white",
                }}
              >
                <span className="fs-12 fw-400 textGrey">Date :</span>
                <div className="dropdown-text1 ms-2" style={{ marginBottom: "4px" }}>
                  <DateRangePicker onChange={handleDateChange} startDate={startDate} endDate={endDate} style={{ border: "none", padding: "0", backgroundColor: "transparent" }} />
                </div>
              </div>

              <div
                className="cursor-pointer ms-1 fs-12 fw-400 textGrey text-decoration-underline d-flex align-items-center justify-content-center"
                style={{ height: "30px" }}
                onClick={resetFilters}
              >
                Reset Filters
              </div>
            </div>

            <div
              className="rounded-3 toggleBg d-flex mt-2"
              style={{
                width: "80px",
                height: "32px",
                backgroundColor: "#FFFFFF",
                padding: "4px",
                gap: "4px",
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid #EBEBEB",
              }}
            >
              <div
                onClick={() => setActive("list")}
                className={`d-flex align-items-center justify-content-center rounded-3 ${active === "list" ? "bg-primary1" : ""}`}
                style={{
                  width: "36px",
                  height: "24px",
                  cursor: "pointer",
                }}
              >
                {active === "list" ? <ListView style={ICON_STYLE} /> : <SelectList style={ICON_STYLE} />}
              </div>

              <div
                onClick={() => setActive("grid")}
                className={`d-flex align-items-center justify-content-center rounded-3 ${active === "grid" ? "bg-primary1" : ""}`}
                style={{
                  width: "36px",
                  height: "24px",
                  cursor: "pointer",
                }}
              >
                {active === "grid" ? <SelectGrid style={ICON_STYLE} /> : <EmailView style={ICON_STYLE} />}
              </div>
            </div>
          </div>
        </div>

        {active === "list" && (
          <div className="w-100 trip-table-wrapper bg-white rounded-4 w-100">
            <div
              style={{
                display: "flex",
                flex: "7",
                paddingLeft: "16px",
                paddingRight: "16px",
                paddingTop: "16px",
                height: "40px",
                backgroundColor: "#FFFFFF",
                border: "1px solid #e5e5e5",
              }}
              className="head3 w-100 justify-content-start align-items-center"
            >
              <p style={{ flex: "1" }} className="fs-13 fw-700 textcolor text-start table-header-p">
                CODE
              </p>
              <p style={{ flex: "1" }} className="fs-13 fw-700 textcolor text-start table-header-p">
                NAME
              </p>
              <p style={{ flex: "1" }} className="fs-13 fw-700 textcolor text-start table-header-p">
                LEVELS
              </p>
              <p style={{ flex: "1" }} className="fs-13 fw-700 textcolor text-start table-header-p">
                CREATED AT
              </p>
            </div>
            <div>
              {isLoading
                ? Array.from({ length: 5 }).map((_, index) => (
                  <div
                    key={index}
                    style={{
                      display: "flex",
                      flex: "10",
                      paddingLeft: "16px",
                      paddingRight: "16px",
                      backgroundColor: "#FFFFFF",
                      borderBottom: "1px solid #D4DDE7",
                      borderLeft: "1px solid #D4DDE7",
                      borderRight: "1px solid #D4DDE7",
                      height: "44px",
                    }}
                    className="head3 w-100 justify-content-center align-items-center"
                  >
                    <div style={{ flex: "1" }} className="d-flex align-items-center">
                      <div className="skeleton skeleton-text" style={{ width: "80px", height: "14px" }} />
                    </div>
                    <div style={{ flex: "1" }} className="d-flex align-items-center">
                      <div className="skeleton skeleton-text" style={{ width: "140px", height: "14px" }} />
                    </div>
                    <div style={{ flex: "1" }} className="d-flex align-items-center">
                      <div className="skeleton skeleton-text" style={{ width: "40px", height: "14px" }} />
                    </div>
                    <div style={{ flex: "1" }} className="d-flex align-items-center">
                      <div className="skeleton skeleton-text" style={{ width: "100px", height: "14px" }} />
                    </div>
                  </div>
                ))
                : (milestonesList ?? []).map((item, index) => (
                  <div
                    key={item.id || index}
                    style={{
                      display: "flex",
                      flex: "10",
                      paddingLeft: "16px",
                      paddingRight: "16px",
                      backgroundColor: hoveredIndex === index ? "#FAFAFA" : "#FFFFFF",
                      borderBottom: "1px solid #D4DDE7",
                      borderLeft: "1px solid #D4DDE7",
                      borderRight: "1px solid #D4DDE7",
                      cursor: "pointer",
                      height: "44px",
                      position: "relative",
                      overflow: "hidden",
                    }}
                    onClick={() => {
                      navigate("/milestone/create", { state: { id: item.id } });
                    }}
                    onMouseEnter={() => setHoveredIndex(index)}
                    onMouseLeave={() => setHoveredIndex(null)}
                    className="head3 w-100 justify-content-center align-items-center"
                  >
                    <p
                      className="fs-13 fw-500 textcolor text-start table-row-p"
                      style={{
                        flex: "1",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        marginTop: "16px",
                      }}
                      title={sanitizeInput(item.code)}
                    >
                      {sanitizeInput(item.code) || "---"}
                    </p>
                    <p
                      className="fs-13 fw-500 textcolor text-start table-row-p"
                      style={{
                        flex: "1",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        marginTop: "16px",
                      }}
                      title={sanitizeInput(item.name)}
                    >
                      {sanitizeInput(item.name) || "---"}
                    </p>
                    <p
                      className="fs-13 fw-500 textcolor text-start table-row-p"
                      style={{
                        flex: "1",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        marginTop: "16px",
                      }}
                      title={sanitizeInput(item.level)}
                    >
                      {sanitizeInput(item.level) || "0"}
                    </p>
                    <p
                      className="fs-13 fw-500 textcolor text-start table-row-p"
                      style={{
                        flex: "1",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        marginTop: "16px",
                      }}
                      title={sanitizeInput(item.created_date)}
                    >
                      {sanitizeInput(item.created_date) || "---"}
                    </p>
                    {hoveredIndex === index && (
                      <div
                        style={{
                          position: "absolute",
                          right: "35px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "start",
                            alignItems: "center",
                          }}
                        >
                          {checkPermission("edit_permission") && (
                            <div
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate("/milestone/create", {
                                  state: { id: item.id },
                                });
                              }}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "28px",
                                height: "28px",
                                borderRadius: "100px",
                                background: "#FFFFFF",
                                cursor: "pointer",
                                border: "0.8px solid #E5E5E5",
                              }}
                            >
                              <img src={editIcon} alt="edit" />
                            </div>
                          )}
                          <span style={{ marginLeft: "16px" }}></span>
                          {checkPermission("delete_permission") && (
                            <div
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteId(item.id);
                              }}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "28px",
                                height: "28px",
                                borderRadius: "100px",
                                background: "#FFFFFF",
                                cursor: "pointer",
                                border: "0.8px solid #E5E5E5",
                              }}
                            >
                              <img src={deleteIcon} alt="delete" />
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
            </div>
          </div>
        )}

        {active === "grid" &&
          (isLoading
            ? Array.from({ length: 5 }).map((_, index) => (
              <div key={index} className="bg-white rounded-4 d-flex flex-wrap align-items-center justify-content-between w-100 mt-3 p-3 mb-3">
                <div className="col-12 col-md-6 col-lg-3 d-flex flex-grow-1 me-2" style={{ borderRight: "1px solid #EBEBEB" }}>
                  <div className="skeleton" style={{ width: "48px", height: "48px", borderRadius: "8px" }} />
                  <div className="d-flex flex-column justify-content-center gap-2 ps-3">
                    <div className="skeleton skeleton-text" style={{ width: "150px", height: "16px" }} />
                    <div className="skeleton skeleton-text-sm" style={{ width: "100px", height: "12px" }} />
                  </div>
                </div>

                <div className="col-12 col-md-6 col-lg-3 ms-3 d-flex flex-column flex-grow-1 align-items-start me-1" style={{ borderRight: "1px solid #EBEBEB" }}>
                  <div className="skeleton skeleton-text-sm mb-2" style={{ width: "120px", height: "12px" }} />
                  <div className="skeleton skeleton-text-sm" style={{ width: "140px", height: "12px" }} />
                </div>

                <div className="d-flex flex-row flex-grow-1 align-items-end justify-content-end gap-2 ps-3">
                  <div className="skeleton" style={{ width: "36px", height: "36px", borderRadius: "50%" }} />
                </div>
              </div>
            ))
            : milestonesList.map((item, index) => (
              <div
                key={item.id || index}
                className="bg-white rounded-4 d-flex flex-wrap align-items-center justify-content-between business-card w-100 p-3 mb-3"
                style={{ cursor: "pointer" }}
                onClick={() => {
                  navigate("/milestone/create", { state: { id: item.id } });
                }}
              >
                <div className="col-12 col-md-6 col-lg-3 d-flex flex-grow-1 me-2" style={{ borderRight: "1px solid #EBEBEB" }}>
                  <div className="profile-wrapper" style={{ background: "#F4F4F4" }}>
                    <img src={milestoneIcon} alt="milestone" className="profile-img" />
                  </div>
                  <div className="d-flex flex-column justify-content-center align-items-start gap-1 ps-3">
                    <div className="textFontColor fs-16 fw-600">{sanitizeInput(item.name) || "-"}</div>
                    <div className="d-flex gap-2 align-items-center">
                      <span className="text-gray fs-12 fw-500">{sanitizeInput(item.code) || ""}</span>
                    </div>
                  </div>
                </div>

                <div className="col-12 col-md-6 col-lg-3 ms-3 d-flex flex-column flex-grow-1 align-items-start justify-content-start me-1" style={{ borderRight: "1px solid #EBEBEB" }}>
                  <div className="d-flex flex-column flex-grow-1 align-items-start">
                    <div className="fs-12 fw-600 cardTextGrey">
                      LEVELS : <span className="fs-14 fw-400 textcolor">{sanitizeInput(item.level) || "0"}</span>
                    </div>
                    <div className="fs-12 fw-600 cardTextGrey mt-1">
                      CREATED AT : <span className="fs-14 fw-400 textcolor">{sanitizeInput(item.created_date) || ""}</span>
                    </div>
                  </div>
                </div>

                <div className="d-flex flex-row flex-grow-1 align-items-end justify-content-end gap-2 ps-3">
                  <div onClick={(e) => e.stopPropagation()}>
                    {(checkPermission("edit_permission") || checkPermission("delete_permission")) && (
                      <Dropdown>
                        <Dropdown.Toggle
                          bsPrefix="custom-toggle"
                          variant="light"
                          id="dropdown-sort"
                          className="d-flex align-items-center justify-content-between rounded-5 bg-white"
                          style={{
                            width: "36px",
                            height: "36px",
                            border: "1px solid #EBEBEB",
                          }}
                        >
                          <span className="d-flex align-items-center px-1 gap-2">
                            <img src={moreIcon} alt="more" />
                          </span>
                        </Dropdown.Toggle>

                        <Dropdown.Menu
                          style={{
                            backgroundColor: "white",
                            borderRadius: "8px",
                            border: "1px solid #EBEBEB",
                            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                          }}
                        >
                          {checkPermission("edit_permission") && (
                            <Dropdown.Item
                              className="fs-14 fw-500 textcolor"
                              onClick={() => {
                                navigate("/milestone/create", {
                                  state: { id: item.id },
                                });
                              }}
                            >
                              Edit
                            </Dropdown.Item>
                          )}
                          {checkPermission("delete_permission") && (
                            <Dropdown.Item
                              className="fs-14 fw-500 textcolor"
                              onClick={() => {
                                setDeleteId(item.id);
                              }}
                            >
                              Delete
                            </Dropdown.Item>
                          )}
                        </Dropdown.Menu>
                      </Dropdown>
                    )}
                  </div>
                </div>
              </div>
            )))}

        <CommonPagination
          itemsPerPage={itemsPerPage}
          setItemsPerPage={setItemsPerPage}
          pageCount={pageCount}
          onPageChange={handlePageClick}
          forcePage={itemOffset}
        />
      </div>

      <CommonSnackbar
        show={snackbar.show}
        onClose={handleSnackbarClose}
        message={snackbar.message}
        isSuccess={snackbar.status}
      />

      <style jsx>{`
        .milestone-list-container {
          padding: 10px;
        }
        .truncate {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .flex-1 {
          flex: 1;
        }
        .action-btn {
          background: #ffffff;
          border: 0.8px solid #e5e5e5;
          border-radius: 50%;
          width: 28px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: transform 0.2s;
        }
        .action-btn:hover {
          transform: scale(1.1);
        }
        .transition-all {
          transition: all 0.2s ease-in-out;
        }
        .table-header {
          position: sticky;
          top: 0;
          z-index: 10;
        }
      `}</style>
    </>
  );
};
